import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, PlayCircle, Copy, CheckCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StrategyDetailsPage = () => {
  const { strategyId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brokers, setBrokers] = useState([]);
  const [showDeployDialog, setShowDeployDialog] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    if (strategyId) {
      fetchStrategy();
      fetchBrokers();
    }
  }, [strategyId]);

  const fetchStrategy = async () => {
    try {
      const response = await axios.get(`${API}/strategies/${strategyId}`, { withCredentials: true });
      setStrategy(response.data);
    } catch (error) {
      // Try from library if not found in user strategies
      try {
        const libraryResponse = await axios.get(`${API}/library`, { withCredentials: true });
        const foundStrategy = libraryResponse.data.find(s => s.id === strategyId);
        if (foundStrategy) {
          setStrategy(foundStrategy);
        } else {
          toast.error('Strategy not found');
          navigate('/dashboard');
        }
      } catch (err) {
        toast.error('Failed to load strategy');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBrokers = async () => {
    try {
      const response = await axios.get(`${API}/brokers`, { withCredentials: true });
      setBrokers(response.data);
    } catch (error) {
      console.error('Failed to fetch brokers:', error);
    }
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      await axios.post(`${API}/strategies/${strategyId}/subscribe`, {}, { withCredentials: true });
      toast.success('Successfully subscribed to strategy!');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const handleDeploy = async () => {
    if (!selectedBroker) {
      toast.error('Please select a broker');
      return;
    }

    try {
      // First subscribe/copy the strategy
      const copyResponse = await axios.post(`${API}/strategies/${strategyId}/subscribe`, {}, { withCredentials: true });
      const copiedStrategy = copyResponse.data;

      // Then deploy it
      await axios.post(`${API}/deploy`, {
        strategy_id: copiedStrategy.id,
        broker_id: selectedBroker
      }, { withCredentials: true });

      toast.success('Strategy deployed successfully!');
      setShowDeployDialog(false);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deploy');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30 flex items-center justify-center">
        <p>Loading strategy...</p>
      </div>
    );
  }

  if (!strategy) {
    return null;
  }

  const tags = ['EarnTheta', 'Pairs', 'Bullish', 'Bearish', 'Directional', 'Positional'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30" data-testid="strategy-details-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">by: {strategy.user_id === 'system-strategies-user' ? 'TukTuk Trading' : 'User'}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8 max-w-5xl">
        <Card className="p-8 bg-white/80 backdrop-blur-sm border-gray-200">
          {/* Title and Tags */}
          <div className="mb-6">
            <h1 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {strategy.name}
            </h1>
            <div className="flex flex-wrap gap-2 mb-4">
              {tags.map((tag) => (
                <Badge key={tag} variant="secondary" className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                  {tag}
                </Badge>
              ))}
            </div>
            <div className="flex gap-3">
              <Button onClick={handleSubscribe} disabled={subscribing} className="bg-indigo-600 hover:bg-indigo-700" data-testid="subscribe-btn">
                <CheckCircle className="w-4 h-4 mr-2" />
                {subscribing ? 'Subscribing...' : 'Subscribe'}
              </Button>
              <Button onClick={() => brokers.length > 0 ? setShowDeployDialog(true) : toast.error('Connect a broker first')} variant="outline" data-testid="deploy-btn">
                <PlayCircle className="w-4 h-4 mr-2" />
                Deploy Now
              </Button>
            </div>
          </div>

          {/* Strategy Description */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Strategy description
            </h2>
            <p className="text-gray-700 leading-relaxed">
              {strategy.description}
            </p>
          </div>

          {/* Key Advantages */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Key Advantages:
            </h2>
            <ol className="space-y-2">
              <li className="text-gray-700">
                <span className="font-semibold">Risk Management:</span> Built-in stop loss and trailing stop loss for capital protection
              </li>
              <li className="text-gray-700">
                <span className="font-semibold">Automated Execution:</span> Conditions checked automatically at specified intervals
              </li>
              <li className="text-gray-700">
                <span className="font-semibold">Proven Indicators:</span> Uses well-tested technical indicators and market conditions
              </li>
              <li className="text-gray-700">
                <span className="font-semibold">Flexible Settings:</span> Customizable parameters for different market conditions
              </li>
            </ol>
          </div>

          {/* Capital Allocation */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Capital Allocation:
            </h2>
            <ol className="space-y-2">
              <li className="text-gray-700">
                100% - Entry positions as per strategy conditions
              </li>
            </ol>
          </div>

          {/* Key Details */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Key Details:
            </h2>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Capital Requirement:</p>
                  <p className="text-lg font-semibold text-gray-900">
                    ₹{(strategy.advanced_settings?.capital_required || 50000).toLocaleString()} per 1 Multiplier
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Instrument Traded:</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {strategy.strategy_set?.entry_positions?.[0]?.underlying || 'NIFTY'} Options
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Type:</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {strategy.strategy_set?.entry_positions?.[0]?.type === 'CE' ? 'Call Options' : 'Mixed'}
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Check Interval:</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {strategy.advanced_settings?.check_conditions_every || '1 minute'}
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Position Size:</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {strategy.strategy_set?.entry_positions?.[0]?.qty || 0} lots
                  </p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Trailing Stop Loss:</p>
                  <p className="text-lg font-semibold text-gray-900">
                    Activate at {strategy.advanced_settings?.trailing_stop_loss_activate || 0}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Entry & Exit Conditions */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Trading Rules:
            </h2>
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <h3 className="font-semibold text-green-900 mb-2">Entry Conditions:</h3>
                <p className="text-sm text-green-800 whitespace-pre-wrap">
                  {strategy.strategy_set?.entry?.conditions_text || 'Based on technical indicators'}
                </p>
              </div>
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <h3 className="font-semibold text-red-900 mb-2">Exit Conditions:</h3>
                <p className="text-sm text-red-800 whitespace-pre-wrap">
                  {strategy.strategy_set?.universal_exit?.conditions_text || 'Stop loss or target based'}
                </p>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 p-6 rounded-lg">
            <h2 className="text-lg font-bold text-amber-900 mb-3">Disclaimer:</h2>
            <ol className="space-y-2 text-sm text-amber-800">
              <li>
                1. TukTuk Trading is not a SEBI-registered investment or financial advisor. Strategies should not be deployed solely based on past performance. Profit and loss updates are based on simulated data and are provided only for informational purposes.
              </li>
              <li>
                2. Please note that trading strategies are not a shortcut to becoming wealthy, and a disciplined approach is essential for long-term results.
              </li>
              <li>
                3. Conduct paper trading for at least one month to understand strategy behavior before live deployment.
              </li>
            </ol>
          </div>
        </Card>
      </div>

      {/* Deploy Dialog */}
      <Dialog open={showDeployDialog} onOpenChange={setShowDeployDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deploy {strategy.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Select Broker</label>
              <Select value={selectedBroker} onValueChange={setSelectedBroker}>
                <SelectTrigger data-testid="deploy-broker-select">
                  <SelectValue placeholder="Choose broker" />
                </SelectTrigger>
                <SelectContent>
                  {brokers.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.broker_name} {b.is_connected ? '✓' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleDeploy} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-deploy-btn">
              Deploy Strategy
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StrategyDetailsPage;
